import type { ConnectionInterrupt } from "#features/conversations/lib/buildPendingConnection.ts";
import { updateMessages } from "#features/conversations/lib/toolApprovalRecords.ts";
import type { ConversationModel } from "#features/conversations/types/ConversationMessageModel.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { QueryClient } from "@tanstack/react-query";

// The conversation's open connection request.
export function findPendingConnection(conversation: ConversationModel | undefined) {
    return conversation?.messages.findLast(({ connection }) => connection?.status === "pending")?.connection;
}

/** Shows the connection request a run ended on, on the message that asked it. */
export function showPendingConnection(queryClient: QueryClient, conversationId: string, messageId: string, connection: ConnectionInterrupt) {
    updateMessages(queryClient, conversationId, message => (message.id === messageId
        ? { ...message, connection: { ...connection, status: "pending" } }
        : message));
}

/** Closes the open request: its card stops offering Dismiss and resumes nothing. The next read replays the same state. */
export function settlePendingConnection(queryClient: QueryClient, conversationId: string, interruptId: string, status: "resolved" | "dismissed") {
    // A read already in flight predates the resume, so it must not bring the request back open.
    void queryClient.cancelQueries({ queryKey: AiQueryKeys.GetConversation(conversationId) });
    updateMessages(queryClient, conversationId, message => (message.connection?.interruptId === interruptId && message.connection.status === "pending"
        ? { ...message, connection: { ...message.connection, status } }
        : message));
}

/**
 * Shows the open request as dismissed when a message is sent past it; core supersedes the open pause on its own.
 * Returns whether the card changed.
 */
export function cancelPendingConnection(queryClient: QueryClient, conversationId: string): boolean {
    const pending = findPendingConnection(queryClient.getQueryData<ConversationModel>(AiQueryKeys.GetConversation(conversationId)));
    if (!pending) {
        return false;
    }
    settlePendingConnection(queryClient, conversationId, pending.interruptId, "dismissed");

    return true;
}
